import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { sanitise } from '../lib/audit';
import { renderApplicationPdfBlob, blobToBase64 } from '../lib/documents/applicationPdf';
import { credentialDateFlags, flagMessage } from '../lib/credentialDates';
import { shrinkForUpload } from '../lib/documents/shrinkImage';
import { useFormDraftBackup } from '../hooks/useFormDraftBackup';
import {
  APPLICATION_INCOMPLETE, APPLICATION_OPEN_HOURS, CERTIFICATION_DOCUMENT, licenseRequired as needsLicense,
} from '../lib/applicationDocuments';

// After a photo is shrunk, nothing honest is this big; a 20 MB scanned PDF is
// better refused with a reason than left to time out on a phone.
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const UPLOAD_TIMEOUT_MS = 120000;

/** A document that could not be sent, and which one, for the message. */
class UploadProblem extends Error {
  constructor(kind, label, size = 0) { super(`${kind}: ${label}`); this.kind = kind; this.label = label; this.size = size; }
}

const POSITION_OPTS = ['rn', 'lpn', 'pca', 'office_admin', 'other'];
// Agency nurses apply here too (2026-09-07): they were previously added by
// hand in Personnel, which skipped every document and produced duplicate
// records. Selecting "agency" pre-selects the Agency hire track when the
// office prepares their bundle — no offer letter, no Gusto, sending agency
// is the employer of record (Policy A-2.4).
const EMPLOYMENT_OPTS = [
  { value: 'employee', label: { en: 'Employee (W2)', es: 'Empleado(a) (W2)' } },
  { value: 'contractor', label: { en: 'Contractor (1099)', es: 'Contratista (1099)' } },
  { value: 'agency', label: { en: 'Agency nurse (employed by a staffing agency)', es: 'Enfermera(o) de agencia (empleado(a) por una agencia)' } },
];

// Every applicant uploads the full document set up front (2026-09-07) so an
// offer can go out the moment the office reviews the application — nothing
// is chased afterwards. License/certificate is only compulsory for RN/LPN/CNA.
// What the applicant typed, kept on their own device until the application
// is received: a refresh, a closed tab or a failed upload no longer throws
// it away. Never the SSN (once sent it is on the application, encrypted) and
// never the files (a browser cannot keep them) — only which documents have
// already arrived, so they are not asked for twice.
const DRAFT_KEY = 'ahfc_careers_application_draft';
const SSN_DIGITS = (v) => (v || '').replace(/\D/g, '');
const formatSsn = (v) => {
  const d = SSN_DIGITS(v).slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const TRANSLATIONS = {
  en: {
    joinOurTeam: 'Join Our Team', tagline: 'A Heart For Care — Employment Application',
    personalInfo: 'Personal Information', firstName: 'First Name', lastName: 'Last Name',
    email: 'Email', phone: 'Phone', address: 'Street Address', city: 'City', state: 'State', zip: 'ZIP Code', dob: 'Date of Birth',
    position: 'Position', positionApplyingFor: 'Position Applying For',
    selectOne: 'Select...',
    rn: 'RN (Registered Nurse)', lpn: 'LPN (Licensed Practical Nurse)', pca: 'PCA / Caregiver', office_admin: 'Office / Administrative', other: 'Other',
    employmentTypeDesired: 'Employment Type Desired',
    workHistory: 'Work History / Relevant Experience', workHistoryPlaceholder: 'Briefly describe your relevant work experience...',
    documents: 'Documents', documentsIntro: 'Please have these ready — we need all of them before an offer can be sent.',
    resume: 'Resume', resumeHint: 'PDF, DOC, or image',
    attached: 'Attached', attachedMany: 'files attached',
    fileEmpty: 'That file came through empty, so it was not attached. On a phone, choose it again from Files or Gallery rather than Camera or a cloud photo.',
    governmentId: "Driver's License or Government ID", governmentIdHint: 'Photo/data page',
    cprCard: 'CPR / First Aid card', cprCardHint: 'Current card, front side',
    cprExpiry: 'CPR card expiry date',
    tbResult: 'TB test result', tbResultHint: 'Within the last 12 months',
    tbDate: 'Date of TB test', tbDateHint: 'The date the test was given or read — not the expiry date. Valid 12 months from this date.',
    licenseExpiry: 'License / certificate expiry date',
    datesFlaggedTitle: 'You can still submit, but please check these dates:',
    receivedDatesFlagged: 'We noticed a problem with some of your document dates. Our team will contact you if we need an updated document:',
    licenseCert: 'Nursing license / certificate', licenseCertHint: 'Required for RN, LPN and CNA',
    licenseCertOptional: 'Nursing license / certificate (if you have one)',
    certifications: 'Other certifications (optional)', certificationsHint: 'You can select multiple files',
    ssn: 'Social Security Number', ssnHint: 'Required for your background check. Stored encrypted; only administrators can view it.',
    errorSsn: 'Please enter a valid 9-digit Social Security Number.',
    consentSignature: 'Consent & Signature',
    consentText: 'By typing your full name below, you certify that the information provided in this application is true and accurate to the best of your knowledge, and you consent to A Heart For Care contacting you regarding this application.',
    signToApply: 'Type your full name to sign', fullLegalName: 'Full legal name',
    submitting: 'Submitting…', submit: 'Submit Application',
    uploading: 'Sending documents… {n} of {total}',
    errorUpload: "Your {doc} did not upload. The connection dropped or was too slow. Press Submit again: the documents that already went through will not be sent twice. If it keeps failing, try again on Wi-Fi or call our office.",
    errorTooLarge: 'Your {doc} is too large to send ({mb} MB). Please choose a smaller file, or take a photo of it instead.',
    alreadySent: 'Already sent',
    draftRestored: 'We kept what you entered earlier on this device. Check it, then press Submit.',
    draftRestoredDocs: 'Documents marked "Already sent" arrived and will not be sent again; attach any others.',
    draftStartOver: 'Start over',
    ssnAlreadyReceived: 'Already received with your application. Leave this blank unless it needs correcting.',
    errorMissingPrefix: 'Please complete the following before submitting: ',
    errorEmail: 'Please enter a valid email address.',
    errorGeneral: 'Something went wrong submitting your application. Please try again or call our office.',
    receivedTitle: 'Application Received!',
    receivedMsg: "Thank you for applying to A Heart For Care. A member of our team will review your application and reach out if there's a fit.",
  },
  es: {
    joinOurTeam: 'Únase a Nuestro Equipo', tagline: 'A Heart For Care — Solicitud de Empleo',
    personalInfo: 'Información Personal', firstName: 'Nombre', lastName: 'Apellido',
    email: 'Correo Electrónico', phone: 'Teléfono', address: 'Dirección', city: 'Ciudad', state: 'Estado', zip: 'Código Postal', dob: 'Fecha de Nacimiento',
    position: 'Puesto', positionApplyingFor: 'Puesto al que Aplica',
    selectOne: 'Seleccione...',
    rn: 'RN (Enfermera Registrada)', lpn: 'LPN (Enfermera Práctica Licenciada)', pca: 'PCA / Cuidador(a)', office_admin: 'Oficina / Administrativo', other: 'Otro',
    employmentTypeDesired: 'Tipo de Empleo Deseado',
    workHistory: 'Historial Laboral / Experiencia Relevante', workHistoryPlaceholder: 'Describa brevemente su experiencia laboral relevante...',
    documents: 'Documentos', documentsIntro: 'Tenga estos documentos listos — necesitamos todos antes de poder enviar una oferta.',
    resume: 'Currículum', resumeHint: 'PDF, DOC, o imagen',
    attached: 'Adjunto', attachedMany: 'archivos adjuntos',
    fileEmpty: 'Ese archivo llegó vacío, así que no se adjuntó. En un teléfono, elíjalo de nuevo desde Archivos o Galería en lugar de la Cámara o una foto en la nube.',
    governmentId: 'Licencia de Conducir o Identificación Oficial', governmentIdHint: 'Página de foto/datos',
    cprCard: 'Tarjeta de RCP / Primeros Auxilios', cprCardHint: 'Tarjeta vigente, lado frontal',
    cprExpiry: 'Fecha de vencimiento de la tarjeta de RCP',
    tbResult: 'Resultado de prueba de TB', tbResultHint: 'De los últimos 12 meses',
    tbDate: 'Fecha de la prueba de TB', tbDateHint: 'La fecha en que se hizo o se leyó la prueba, no la fecha de vencimiento. Válida por 12 meses desde esta fecha.',
    licenseExpiry: 'Fecha de vencimiento de la licencia / certificado',
    datesFlaggedTitle: 'Puede enviar de todos modos, pero revise estas fechas:',
    receivedDatesFlagged: 'Notamos un problema con las fechas de algunos de sus documentos. Nuestro equipo se comunicará con usted si necesitamos un documento actualizado:',
    licenseCert: 'Licencia / certificado de enfermería', licenseCertHint: 'Obligatorio para RN, LPN y CNA',
    licenseCertOptional: 'Licencia / certificado de enfermería (si lo tiene)',
    certifications: 'Otras certificaciones (opcional)', certificationsHint: 'Puede seleccionar varios archivos',
    ssn: 'Número de Seguro Social', ssnHint: 'Requerido para su verificación de antecedentes. Se guarda cifrado; solo los administradores pueden verlo.',
    errorSsn: 'Por favor ingrese un Número de Seguro Social válido de 9 dígitos.',
    consentSignature: 'Consentimiento y Firma',
    consentText: 'Al escribir su nombre completo a continuación, usted certifica que la información proporcionada en esta solicitud es verdadera y precisa a su leal saber y entender, y da su consentimiento para que A Heart For Care se comunique con usted respecto a esta solicitud.',
    signToApply: 'Escriba su nombre completo para firmar', fullLegalName: 'Nombre legal completo',
    submitting: 'Enviando…', submit: 'Enviar Solicitud',
    uploading: 'Enviando documentos… {n} de {total}',
    errorUpload: 'Su {doc} no se pudo subir. La conexión se cortó o fue demasiado lenta. Presione Enviar de nuevo: los documentos que ya llegaron no se enviarán dos veces. Si sigue fallando, inténtelo con Wi-Fi o llame a nuestra oficina.',
    errorTooLarge: 'Su {doc} es demasiado grande para enviar ({mb} MB). Elija un archivo más pequeño o tómele una foto.',
    alreadySent: 'Ya enviado',
    draftRestored: 'Guardamos lo que escribió antes en este dispositivo. Revíselo y presione Enviar.',
    draftRestoredDocs: 'Los documentos marcados "Ya enviado" llegaron y no se enviarán de nuevo; adjunte los demás.',
    draftStartOver: 'Empezar de nuevo',
    ssnAlreadyReceived: 'Ya lo recibimos con su solicitud. Déjelo en blanco a menos que necesite corregirlo.',
    errorMissingPrefix: 'Por favor complete lo siguiente antes de enviar: ',
    errorEmail: 'Por favor ingrese una dirección de correo electrónico válida.',
    errorGeneral: 'Algo salió mal al enviar su solicitud. Por favor intente de nuevo o llame a nuestra oficina.',
    receivedTitle: '¡Solicitud Recibida!',
    receivedMsg: 'Gracias por postularse a A Heart For Care. Un miembro de nuestro equipo revisará su solicitud y se pondrá en contacto si hay una buena opción.',
  },
};

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '',
  address: '', city: '', state: 'GA', zip: '', date_of_birth: '',
  position_applied_for: '', employment_type_desired: '',
  work_history: '', consent_signature_name: '', ssn: '',
  cpr_expiry_date: '', tb_test_date: '', license_expiry_date: '',
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function ApplyForm() {
  const [lang, setLang] = useState('en');
  const t = TRANSLATIONS[lang];
  const [form, setForm] = useState(EMPTY_FORM);
  const [resumeFile, setResumeFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [certFiles, setCertFiles] = useState([]);
  const [cprFile, setCprFile] = useState(null);
  const [tbFile, setTbFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  // Which document inputs were handed an empty file (see pickFile).
  const [emptyPick, setEmptyPick] = useState({});

  // Phones' file pickers sometimes hand back nothing usable — a photo still in
  // the cloud, or one from the Camera option after the browser was paused —
  // and the browser's own label then reads "No file chosen" with no reason.
  // A 0-byte file is refused here and the applicant told why, instead of
  // submitting an empty upload. (Pamela Smooth, 2026-10-22.)
  const pickFile = (key, setter) => (e) => {
    const f = e.target.files?.[0] || null;
    const empty = !!f && f.size === 0;
    setEmptyPick(p => ({ ...p, [key]: empty }));
    setter(empty ? null : f);
  };
  const pickFiles = (e) => {
    const all = Array.from(e.target.files || []);
    const kept = all.filter(f => f.size > 0);
    setEmptyPick(p => ({ ...p, certs: kept.length < all.length }));
    setCertFiles(kept);
  };
  const licenseRequired = needsLicense(form.position_applied_for);

  // The application this page has saved (status incomplete until every
  // document is on it), and the documents that have arrived: key → { name, url }.
  const [applicationId, setApplicationId] = useState(null);
  const [sent, setSent] = useState({});
  const [restored, setRestored] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const draftData = useMemo(() => ({ form: { ...form, ssn: '' }, applicationId, sent, lang }), [form, applicationId, sent, lang]);
  const { readBackup, clearBackup } = useFormDraftBackup({ storageKey: DRAFT_KEY, data: draftData, enabled: draftReady, debounceMs: 800 });
  useEffect(() => {
    const d = readBackup();
    const fresh = d && Date.now() - Date.parse(d._savedAt || 0) < APPLICATION_OPEN_HOURS * 3600000;
    if (fresh && d.data) {
      const { form: f, applicationId: id, sent: done, lang: l } = d.data;
      if (f) setForm(p => ({ ...p, ...f, ssn: '' }));
      if (id) setApplicationId(id);
      if (done) setSent(done);
      if (l === 'en' || l === 'es') setLang(l);
      setRestored(true);
    } else if (d) {
      clearBackup();
    }
    setDraftReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const startOver = () => {
    clearBackup();
    setForm(EMPTY_FORM); setApplicationId(null); setSent({}); setRestored(false); setError('');
    setResumeFile(null); setIdFile(null); setCprFile(null); setTbFile(null); setLicenseFile(null); setCertFiles([]);
  };
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const dateFlags = credentialDateFlags(form, { licenseRequired });
  const dateWarning = (field) => {
    const f = dateFlags.find(x => x.field === field);
    return f ? flagMessage(f, lang) : null;
  };

  // A request that never answers would leave the button on "Submitting…" for
  // ever; two minutes is ample for a shrunk photo on a slow connection.
  const uploadOne = async (file, applicantName, appId, document) => {
    const base64 = await fileToBase64(file);
    const call = supabase.functions.invoke('upload-staff-document', {
      body: {
        fileBase64: base64,
        filename: file.name,
        applicantName,
        applicationId: appId,
        document,
        contentType: file.type || 'application/octet-stream',
      },
    });
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Upload timed out')), UPLOAD_TIMEOUT_MS));
    const { data, error: err } = await Promise.race([call, timeout]);
    if (err || !data?.success) throw new Error(data?.error || err?.message || `Upload failed for ${file.name}`);
    return data.url;
  };

  // Documents go one at a time, photos shrunk first, each tried twice, and
  // each is written onto the saved application as it arrives. A document
  // that arrived is remembered (in `done`, and in the draft), so pressing
  // Submit again after a failure sends only what is still missing. Until
  // 2026-10-27 all five went at once at full size before anything was saved;
  // over mobile data the photos never arrived and the applicant left no
  // record at all (Christina Chambers, four attempts).
  const [progress, setProgress] = useState(null);   // { n, total } while sending
  const uploadDocuments = async (docs, applicantName, appId, done) => {
    const pending = docs.filter(d => d.file && !done[d.key]);
    for (let i = 0; i < pending.length; i++) {
      const { key, document, label, file } = pending[i];
      setProgress({ n: i + 1, total: pending.length });
      const small = await shrinkForUpload(file);
      if (small.size > MAX_UPLOAD_BYTES) throw new UploadProblem('tooLarge', label, small.size);
      let url;
      try { url = await uploadOne(small, applicantName, appId, document); }
      catch {
        try { url = await uploadOne(small, applicantName, appId, document); }
        catch { throw new UploadProblem('failed', label); }
      }
      done[key] = { name: file.name, url };
      setSent(p => ({ ...p, [key]: done[key] }));
    }
  };

  // Marking it received is checked on our side: complete-staff-application
  // refuses unless every required document is on the application.
  const completeApplication = async (appId, fields, ssn) => {
    const call = supabase.functions.invoke('complete-staff-application', { body: { applicationId: appId, fields, ssn } });
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out')), UPLOAD_TIMEOUT_MS));
    const { data, error: err } = await Promise.race([call, timeout]);
    if (data) return data;
    try { return await err?.context?.json(); } catch { return { success: false, error: err?.message }; }
  };

  // All fields are required except Certifications/Licenses (explicitly
  // optional) — builds a specific list of what's missing rather than a
  // generic "fill in required fields" message.
  const findMissing = () => {
    const missing = [];
    const req = (val, label) => { if (!val || !String(val).trim()) missing.push(label); };
    req(form.first_name, t.firstName);
    req(form.last_name, t.lastName);
    req(form.email, t.email);
    req(form.phone, t.phone);
    req(form.address, t.address);
    req(form.city, t.city);
    req(form.state, t.state);
    req(form.zip, t.zip);
    req(form.date_of_birth, t.dob);
    req(form.position_applied_for, t.positionApplyingFor);
    req(form.employment_type_desired, t.employmentTypeDesired);
    req(form.work_history, t.workHistory);
    req(form.consent_signature_name, t.signToApply);
    // A document already sent with this application counts as attached.
    if (!resumeFile && !sent.resume) missing.push(t.resume);
    if (!idFile && !sent.id) missing.push(t.governmentId);
    if (!cprFile && !sent.cpr) missing.push(t.cprCard);
    req(form.cpr_expiry_date, t.cprExpiry);
    if (!tbFile && !sent.tb) missing.push(t.tbResult);
    req(form.tb_test_date, t.tbDate);
    if (licenseRequired && !licenseFile && !sent.license) missing.push(t.licenseCert);
    if (licenseRequired) req(form.license_expiry_date, t.licenseExpiry);
    // The SSN went with the saved application; it is asked for only once.
    if (!applicationId && !SSN_DIGITS(form.ssn)) missing.push(t.ssn);
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) { setSubmitted(true); return; }
    const missing = findMissing();
    if (missing.length > 0) {
      setError(t.errorMissingPrefix + missing.join(', '));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(t.errorEmail);
      return;
    }
    const ssnDigits = SSN_DIGITS(form.ssn);
    if ((!applicationId || ssnDigits) && ssnDigits.length !== 9) {
      setError(t.errorSsn);
      return;
    }
    // Out-of-date credentials warn, never block — the applicant sees which
    // date is off beside the field, and the reviewer sees the same flags on
    // the application (lib/credentialDates.js).
    setSubmitting(true);
    setError('');
    try {
      const applicantName = `${form.first_name.trim()} ${form.last_name.trim()}`;
      const consentSignedAt = new Date().toISOString();
      const fields = {
        first_name: sanitise(form.first_name.trim()),
        last_name: sanitise(form.last_name.trim()),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: sanitise(form.address),
        city: sanitise(form.city),
        state: form.state.trim(),
        zip: form.zip.trim(),
        date_of_birth: form.date_of_birth,
        position_applied_for: form.position_applied_for,
        employment_type_desired: form.employment_type_desired,
        work_history: sanitise(form.work_history),
        cpr_expiry_date: form.cpr_expiry_date || null,
        tb_test_date: form.tb_test_date || null,
        license_expiry_date: licenseRequired && form.license_expiry_date ? form.license_expiry_date : null,
        consent_signature_name: sanitise(form.consent_signature_name.trim()),
        consent_signed_at: consentSignedAt,
        form_language: lang,
      };

      // 1. Save the application before anything else, as incomplete, so an
      //    applicant whose documents then fail is still on record (the
      //    office sees them in the Onboarding Pipeline and the Digest).
      //    The id is generated here rather than read back: anonymous
      //    applicants have no SELECT grant on staff_applications (by design),
      //    and asking for the row back made every public insert fail
      //    (2026-07-20). A reply lost on the way back means a retry meets its
      //    own row, which is fine.
      let appId = applicationId;
      if (!appId) {
        appId = crypto.randomUUID();
        const { error: insertErr } = await supabase.from('staff_applications').insert([{
          id: appId, ...fields, status: APPLICATION_INCOMPLETE, certification_urls: [], ssn_input: ssnDigits,
        }]);
        if (insertErr && insertErr.code !== '23505') throw insertErr;
        setApplicationId(appId);
      }

      // 2. The documents, one at a time, each written onto the application.
      const done = { ...sent };
      await uploadDocuments([
        { key: 'resume', document: 'resume', label: t.resume, file: resumeFile },
        { key: 'id', document: 'id', label: t.governmentId, file: idFile },
        { key: 'cpr', document: 'cpr', label: t.cprCard, file: cprFile },
        { key: 'tb', document: 'tb', label: t.tbResult, file: tbFile },
        { key: 'license', document: 'license', label: t.licenseCert, file: licenseRequired ? licenseFile : null },
        ...certFiles.map(f => ({ key: `cert:${f.name}`, document: CERTIFICATION_DOCUMENT, label: f.name, file: f })),
      ], applicantName, appId, done);
      setProgress(null);

      // 3. Received — only if every required document is on it.
      const result = await completeApplication(appId, fields, ssnDigits || undefined);
      if (!result?.success) {
        const labels = { resume: t.resume, id: t.governmentId, cpr: t.cprCard, tb: t.tbResult, license: t.licenseCert };
        if (Array.isArray(result?.missing) && result.missing.length) {
          // The server does not have a document the page thought it sent.
          for (const k of result.missing) delete done[k];
          setSent(done);
          throw new UploadProblem('failed', labels[result.missing[0]] || result.missing[0]);
        }
        throw new Error(result?.error || 'The application could not be completed.');
      }
      clearBackup();
      setSubmitted(true);

      // The applicant's confirmation email comes from the same trigger as the
      // office's notice, once the application is marked received.
      // The office hears about it in the team Space from the database trigger
      // (notify_office, when the application becomes received), not from here.

      // Best-effort — a signed PDF of the application, filed to Drive for
      // the record. Never blocks the "received" screen already shown above;
      // a PDF-generation hiccup shouldn't make the applicant think their
      // submission failed.
      try {
        const app = {
          id: appId, ...fields, status: 'pending',
          resume_url: done.resume?.url || null, id_document_url: done.id?.url || null,
          cpr_card_url: done.cpr?.url || null, tb_result_url: done.tb?.url || null,
          license_cert_url: done.license?.url || null,
          certification_urls: Object.entries(done).filter(([k]) => k.startsWith('cert:')).map(([, v]) => ({ label: v.name, url: v.url })),
        };
        const blob = await renderApplicationPdfBlob({ app, recordId: appId });
        const fileBase64 = await blobToBase64(blob);
        await supabase.functions.invoke('upload-staff-document', {
          body: {
            fileBase64,
            filename: `Application_${applicantName.replace(/[^a-z0-9]+/gi, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`,
            applicantName,
            applicationId: appId,
            contentType: 'application/pdf',
          },
        });
      } catch (pdfErr) {
        console.error('Application PDF filing failed (non-blocking):', pdfErr);
      }
    } catch (err) {
      console.error('Application submit error:', err);
      if (err instanceof UploadProblem) {
        setError(err.kind === 'tooLarge'
          ? t.errorTooLarge.replace('{doc}', err.label).replace('{mb}', (err.size / 1048576).toFixed(1))
          : t.errorUpload.replace('{doc}', err.label));
      } else {
        setError(t.errorGeneral);
      }
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const LangToggle = () => (
    <div style={{ display: 'inline-flex', background: 'var(--border)', borderRadius: 8, padding: 3, marginTop: 14 }}>
      {['en', 'es'].map(l => (
        <button key={l} type="button" onClick={() => setLang(l)} style={{
          padding: '6px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
          background: lang === l ? 'var(--navy)' : 'transparent',
          color: lang === l ? '#fff' : 'var(--text2)',
        }}>
          {l === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
        </button>
      ))}
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: 'var(--accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 480, width: '100%', textAlign: 'center', border: '1px solid var(--border)' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" fill="none" stroke="var(--success)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 600, marginBottom: 12 }}>{t.receivedTitle}</h2>
        <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>{t.receivedMsg}</p>
        {dateFlags.length > 0 && (
          <div style={{ textAlign: 'left', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginTop: 16, fontSize: 13, color: 'var(--text)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.receivedDatesFlagged}</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>{dateFlags.map(f => <li key={f.field}>{flagMessage(f, lang)}</li>)}</ul>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--accent-bg)', fontFamily: 'DM Sans, sans-serif', padding: '24px 16px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo.jpg" alt="A Heart For Care" style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover', marginBottom: 12, boxShadow: '0 4px 16px rgba(13,43,78,0.15)' }} />
          <h1 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 600, marginBottom: 4 }}>{t.joinOurTeam}</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>{t.tagline}</p>
          <LangToggle />
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', padding: '28px 32px' }}>
          <div style={{ display: 'none' }} aria-hidden="true">
            <input tabIndex="-1" autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
          </div>

          {restored && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
              <span>{t.draftRestored}{Object.keys(sent).length > 0 ? ` ${t.draftRestoredDocs}` : ''}</span>
              <button type="button" onClick={startOver} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, whiteSpace: 'nowrap', padding: 0 }}>{t.draftStartOver}</button>
            </div>
          )}
          {error && <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--danger)' }}>{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <SectionTitle>{t.personalInfo}</SectionTitle>
            <Row>
              <Field label={t.firstName} required><input className="form-input" value={form.first_name} onChange={set('first_name')} /></Field>
              <Field label={t.lastName} required><input className="form-input" value={form.last_name} onChange={set('last_name')} /></Field>
            </Row>
            <Row>
              <Field label={t.email} required><input type="email" className="form-input" value={form.email} onChange={set('email')} /></Field>
              <Field label={t.phone} required><input className="form-input" value={form.phone} onChange={set('phone')} /></Field>
            </Row>
            <Row>
              <Field label={t.address} required><input className="form-input" value={form.address} onChange={set('address')} /></Field>
              <Field label={t.dob} required><input type="date" className="form-input" value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
            </Row>
            <Row>
              <Field label={t.city} required><input className="form-input" value={form.city} onChange={set('city')} /></Field>
              <Field label={t.state} required><input className="form-input" value={form.state} onChange={set('state')} maxLength={2} style={{ textTransform: 'uppercase' }} /></Field>
              <Field label={t.zip} required><input className="form-input" value={form.zip} onChange={set('zip')} maxLength={10} /></Field>
            </Row>

            <SectionTitle>{t.position}</SectionTitle>
            <Row>
              <Field label={t.positionApplyingFor} required>
                <select className="form-select" value={form.position_applied_for} onChange={set('position_applied_for')}>
                  <option value="" disabled>{t.selectOne}</option>
                  {POSITION_OPTS.map(o => <option key={o} value={o}>{t[o]}</option>)}
                </select>
              </Field>
              <Field label={t.employmentTypeDesired} required>
                <select className="form-select" value={form.employment_type_desired} onChange={set('employment_type_desired')}>
                  <option value="" disabled>{t.selectOne}</option>
                  {EMPLOYMENT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label[lang]}</option>)}
                </select>
              </Field>
            </Row>
            <Field label={t.workHistory} required>
              <textarea className="form-textarea" rows={4} value={form.work_history} onChange={set('work_history')} placeholder={t.workHistoryPlaceholder} />
            </Field>

            <SectionTitle>{t.documents}</SectionTitle>
            <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>{t.documentsIntro}</p>
            <Row>
              <Field label={t.resume} required hint={t.resumeHint}>
                <input type="file" accept="image/*,application/pdf,.doc,.docx" className="form-input" onChange={pickFile('resume', setResumeFile)} />
                <Attached file={resumeFile} sentName={sent.resume?.name} empty={emptyPick.resume} t={t} />
              </Field>
              <Field label={t.governmentId} required hint={t.governmentIdHint}>
                <input type="file" accept="image/*,application/pdf" className="form-input" onChange={pickFile('id', setIdFile)} />
                <Attached file={idFile} sentName={sent.id?.name} empty={emptyPick.id} t={t} />
              </Field>
            </Row>
            <Row>
              <Field label={t.cprCard} required hint={t.cprCardHint}>
                <input type="file" accept="image/*,application/pdf" className="form-input" onChange={pickFile('cpr', setCprFile)} />
                <Attached file={cprFile} sentName={sent.cpr?.name} empty={emptyPick.cpr} t={t} />
              </Field>
              <Field label={t.cprExpiry} required warning={dateWarning('cpr_expiry_date')}>
                <input type="date" className="form-input" value={form.cpr_expiry_date} onChange={set('cpr_expiry_date')} />
              </Field>
            </Row>
            <Row>
              <Field label={t.tbResult} required hint={t.tbResultHint}>
                <input type="file" accept="image/*,application/pdf" className="form-input" onChange={pickFile('tb', setTbFile)} />
                <Attached file={tbFile} sentName={sent.tb?.name} empty={emptyPick.tb} t={t} />
              </Field>
              <Field label={t.tbDate} required hint={t.tbDateHint} warning={dateWarning('tb_test_date')}>
                <input type="date" className="form-input" value={form.tb_test_date} onChange={set('tb_test_date')} />
              </Field>
            </Row>
            <Row>
              <Field label={licenseRequired ? t.licenseCert : t.licenseCertOptional} required={licenseRequired} hint={t.licenseCertHint}>
                <input type="file" accept="image/*,application/pdf" className="form-input" onChange={pickFile('license', setLicenseFile)} />
                <Attached file={licenseFile} sentName={sent.license?.name} empty={emptyPick.license} t={t} />
              </Field>
              <Field label={t.licenseExpiry} required={licenseRequired} warning={dateWarning('license_expiry_date')}>
                <input type="date" className="form-input" value={form.license_expiry_date} onChange={set('license_expiry_date')} />
              </Field>
            </Row>
            <Field label={t.certifications} hint={t.certificationsHint}>
              <input type="file" multiple accept="image/*,application/pdf" className="form-input" onChange={pickFiles} />
              <Attached files={certFiles} sentNames={Object.entries(sent).filter(([k]) => k.startsWith('cert:')).map(([, v]) => v.name)} empty={emptyPick.certs} t={t} />
            </Field>
            <Field label={t.ssn} required={!applicationId} hint={applicationId ? t.ssnAlreadyReceived : t.ssnHint}>
              <input className="form-input" inputMode="numeric" autoComplete="off" placeholder="___-__-____" maxLength={11}
                value={form.ssn} onChange={e => setForm(p => ({ ...p, ssn: formatSsn(e.target.value) }))} />
            </Field>

            <SectionTitle>{t.consentSignature}</SectionTitle>
            <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>
              {t.consentText}
            </p>
            <Field label={t.signToApply} required>
              <input className="form-input" value={form.consent_signature_name} onChange={set('consent_signature_name')} placeholder={t.fullLegalName} />
            </Field>

            {dateFlags.length > 0 && (
              <div style={{ marginTop: 20, background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text)' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.datesFlaggedTitle}</div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>{dateFlags.map(f => <li key={f.field}>{flagMessage(f, lang)}</li>)}</ul>
              </div>
            )}
            <button type="submit" disabled={submitting} style={{ width: '100%', marginTop: 20, padding: '12px 0', background: submitting ? 'var(--border2)' : 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: submitting ? 'default' : 'pointer', fontFamily: 'inherit' }}>
              {submitting ? (progress ? t.uploading.replace('{n}', progress.n).replace('{total}', progress.total) : t.submitting) : t.submit}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '20px 0 12px', paddingTop: 12, borderTop: '1px solid var(--border)' }}>{children}</div>;
}

// What the form actually holds for a document input, in our own words. The
// browser's label ("No file chosen") is not reliable on phones, so the
// applicant sees the file we will upload — or why we refused one.
function Attached({ file, files, sentName, sentNames = [], empty, t }) {
  const list = files ? files : (file ? [file] : []);
  // Sent already, on an earlier attempt from this device, and not re-picked.
  const already = list.length ? [] : (sentName ? [sentName] : sentNames);
  return (
    <>
      {already.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--success)', fontWeight: 500, overflowWrap: 'anywhere' }}>
          ✓ {t.alreadySent}: {already.join(', ')}
        </div>
      )}
      {list.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--success)', fontWeight: 500, overflowWrap: 'anywhere' }}>
          ✓ {list.length === 1 ? `${t.attached}: ${list[0].name}` : `${list.length} ${t.attachedMany}`}
        </div>
      )}
      {empty && (
        <div role="alert" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: 'var(--text)', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 6, padding: '6px 8px' }}>
          ⚠ {t.fileEmpty}
        </div>
      )}
    </>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>;
}

function Field({ label, required, hint, warning, children }) {
  return (
    <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
      <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
      {children}
      {hint && <div className="form-hint">{hint}</div>}
      {warning && <div role="alert" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: 'var(--text)', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 6, padding: '6px 8px' }}>⚠ {warning}</div>}
    </div>
  );
}
